import { useDispatch, useSelector } from "react-redux";
import TextInput from "../../components/TextInput";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import {
  selectStatefulCosts,
  setFormAnswer,
  setIdentification,
} from "../../ducks/checkoutSlice";
import { CPFCNPJInput, PhoneInput } from "../../components/NumberInput";
import CheckoutSection from "../../components/CheckoutSection";
import toPriceDisplay from "../../utils/toPriceDisplay";
import CheckoutCosts from "../CheckoutCosts";
import CheckoutServerError from "../CheckoutServerError";
import InLocoForm from "../InLocoForm";

// Celular com DDD e nono dígito: é por ele que a loja chama o cliente.
function phoneError(telephone) {
  if (telephone.length === 0) return null;
  const isMobile = telephone.length === 11 && telephone.substring(2, 3) === "9";
  return isMobile ? null : "Celular inválido";
}

/**
 * Checkout do QR Code no layout do celular: as perguntas configuradas no
 * autoatendimento (local, nome, CPF) viram seções da página em vez do passo a
 * passo em tela cheia do totem, e o pedido sai pelo rodapé fixo.
 */
export default function SelfCheckoutQRCodeForm({
  askForLocation,
  askForName,
  askForCpf,
  onSubmit,
}) {
  const dispatch = useDispatch();
  const costs = useSelector(selectStatefulCosts);
  const {
    name,
    telephone,
    isIdentificationEnable,
    identification,
    identificationError,
    isSubmitting,
  } = useSelector((state) => state.checkout);

  const nameError =
    name.length > 0 && name.trim().length < 2 ? "Mínimo 2 letras" : null;
  const telephoneError = phoneError(telephone);

  // O primeiro problema vira o texto do botão, como no checkout do delivery.
  const pending = [
    askForName && name.trim().length < 2 && "Informe seu nome",
    askForName && telephone.length !== 11 && "Informe o celular",
    askForName && telephoneError,
    askForCpf && isIdentificationEnable && !identification && "Informe o CPF",
    askForCpf && isIdentificationEnable && identificationError,
  ].find(Boolean);

  const setAnswer = (field) => (event) =>
    dispatch(setFormAnswer({ name: field, value: event.target.value }));

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    if (pending || isSubmitting) return;
    onSubmit();
  };

  return (
    <>
      {askForLocation && (
        <CheckoutSection title="Como vai ser?">
          <InLocoForm />
        </CheckoutSection>
      )}

      {(askForName || askForCpf) && (
        <CheckoutSection title="Seus dados">
          <Form className="checkout-form" onSubmit={handleSubmit} noValidate>
            {askForName && (
              <>
                <TextInput
                  label="Nome"
                  error={!!nameError}
                  helperText={nameError}
                  value={name}
                  onChange={setAnswer("name")}
                  inputProps={{ maxLength: 64, autoComplete: "name" }}
                  required
                />
                <TextInput
                  label="Celular com DDD"
                  error={!!telephoneError}
                  helperText={
                    telephoneError || "Para avisar quando ficar pronto"
                  }
                  value={telephone}
                  onChange={setAnswer("telephone")}
                  inputComponent={PhoneInput}
                  inputProps={{ autoComplete: "tel-national", type: "tel" }}
                  required
                />
              </>
            )}
            {askForCpf && (
              <Form.Check
                type="switch"
                id="qrcode-identification-switch"
                className="checkout-form__switch"
                checked={!!isIdentificationEnable}
                onChange={(event) =>
                  dispatch(
                    setFormAnswer({
                      name: "isIdentificationEnable",
                      value: event.target.checked,
                    }),
                  )
                }
                label="CPF ou CNPJ na nota?"
              />
            )}
            {askForCpf && isIdentificationEnable && (
              <TextInput
                label="CPF ou CNPJ"
                error={!!identificationError}
                helperText={identificationError}
                value={identification}
                onChange={(event) =>
                  dispatch(setIdentification(event.target.value))
                }
                inputComponent={CPFCNPJInput}
                inputProps={{ inputMode: "numeric" }}
                required
              />
            )}
            <button type="submit" className="d-none" tabIndex={-1} />
          </Form>
        </CheckoutSection>
      )}

      {costs.showSummary && (
        <CheckoutSection title="Resumo">
          <CheckoutCosts />
        </CheckoutSection>
      )}

      <footer className="checkout-footer fixed-bottom">
        <div className="checkout-footer__total">
          <span className="checkout-footer__total-label">Total</span>
          <strong>{toPriceDisplay(costs.total)}</strong>
        </div>
        <button
          type="button"
          className="btn btn-shop checkout-footer__cta"
          disabled={!!pending || isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting && (
            <Spinner animation="border" size="sm" className="me-2" />
          )}
          <span className="checkout-footer__label">
            {isSubmitting ? "Enviando..." : pending || "Fazer pedido"}
          </span>
        </button>
      </footer>
      <CheckoutServerError />
    </>
  );
}
